import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase_client';

interface TeamMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (member: any) => void;
  editingMember?: any;
  selectedCompany: any;
  currentUserRoleId: number;
  teamMembers?: any[];
}

export const TeamMemberModal: React.FC<TeamMemberModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editingMember,
  selectedCompany,
  currentUserRoleId,
  teamMembers = []
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role_id: 5,
    countryPhoneCode: '+60',
    phoneNumber: ''
  });
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [countries, setCountries] = useState<any[]>([]);
  const [countryPhoneCode, setCountryPhoneCode] = useState('+60');
  const [roleIdToName, setRoleIdToName] = useState<{ [key: number]: string }>({});
  const [resettingPassword, setResettingPassword] = useState(false);
  const [rolePermissions, setRolePermissions] = useState<{ [roleId: number]: { category: string; permissions: string[] }[] }>({});

  // Available roles based on current user's role
  const getAvailableRoles = (): number[] => {
    const baseRoles = (() => {
      switch (currentUserRoleId) {
        case 1:
          return [1,2,3,4,5];
        case 2:
          return [2,3,4,5];
        case 3:
          return [3,4,5];
        case 4:
          return [4,5];
        default:
          return [5];
      }
    })();

    // Filter out owner role if owner already exists (unless editing the current owner)
    if (baseRoles.includes(1)) {
      const existingOwner = teamMembers.find(m => m.role_id === 1);
      if (existingOwner && (!editingMember || editingMember.id !== existingOwner.id)) {
        return baseRoles.filter(roleId => roleId !== 1);
      }
    }

    return baseRoles;
  };

  const canEditField = (field: string): boolean => {
    // Email cannot be changed by anyone when editing
    if (editingMember && field === 'email') {
      return false;
    }

    // If user can manage team, they can edit everything
    if ([1, 2].includes(currentUserRoleId)) {
      return true;
    }
    
    // If it's their own profile, they can only edit certain fields
    if (editingMember?.isCurrentUser) {
      switch (field) {
        case 'phone':
          return true;
        case 'name':
          return true;
        case 'email':
          return false;
        case 'role':
          return false; // Cannot change role
        default:
          return false;
      }
    }
    return false;
  };

  const canResetPassword = (): boolean => {
    // Owner and Admin can reset anyone's password
    if ([1, 2].includes(currentUserRoleId)) {
      return true;
    }
    
    // Users can reset their own password
    if (editingMember?.isCurrentUser) {
      return true;
    }
    
    return false;
  };

  // Reset form when modal opens/closes or editing member changes
  useEffect(() => {
    if (isOpen) {
      if (editingMember) {
        // Parse phone number using countries data
        const phone = editingMember.phone || '';
        let extractedCode = '+60'; // default
        let phoneWithoutCode = phone;
        
        // Find matching country code from database
        for (const country of countries) {
            if (phone.startsWith(country.country_phone_code)) {
                extractedCode = country.country_phone_code;
                phoneWithoutCode = phone.substring(country.country_phone_code.length).trim();
                break;
            }
        }
        
        setFormData({
            name: editingMember.name || '',
            email: editingMember.email || '',
            phone: phoneWithoutCode,
            role_id: editingMember.role_id || 5,
            countryPhoneCode: countries.find(c => c.country_phone_code === extractedCode)?.iso || '+60',
            phoneNumber: phoneWithoutCode
        });
        setCountryPhoneCode(extractedCode);
      } else {
        // Reset form for new invitation
        setFormData({
          name: '',
          email: '',
          phone: '',
          role_id: 5,
          countryPhoneCode: '+60',
          phoneNumber: ''
        });
        setCountryPhoneCode('+60');
      }
      setError(null);
    }
  }, [isOpen, editingMember, countries, currentUserRoleId, teamMembers]);

  const fetchRoleMapping = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('roles')
        .select('id, role_name')
        .order('id');
      
      if (error) {
        console.error('Error fetching roles:', error);
        return;
      }
      
      const mapping: { [key: string]: number } = {};
      const idToName: { [key: number]: string } = {};
      
      data?.forEach(role => {
        mapping[role.role_name.toLowerCase()] = role.id;
        idToName[role.id] = role.role_name.toLowerCase();
      });
      
      setRoleIdToName(idToName);
    } catch (error) {
      console.error('Error fetching role mapping:', error);
    }
  }, []);

  const handleRoleChange = (roleId: number) => {
    // Prevent changing FROM owner role
    if (editingMember && editingMember.role_id === 1 && roleId !== 1) {
      setError('Owner role cannot be changed. There must always be one owner in the organization.');
      return;
    }

    // Prevent changing TO owner role if owner already exists
    if (roleId === 1 && editingMember) {
      const existingOwner = teamMembers.find(m => m.role_id === 1 && m.id !== editingMember.id);
      if (existingOwner) {
        setError('Cannot assign owner role. There is already an owner in this organization. Only one owner is allowed.');
        return;
      }
    }

    // Prevent inviting as owner if owner already exists
    if (roleId === 1 && !editingMember) {
      const existingOwner = teamMembers.find(m => m.role_id === 1);
      if (existingOwner) {
        setError('Cannot invite as owner. There is already an owner in this organization. Only one owner is allowed.');
        return;
      }
    }

    setFormData(prev => ({ 
      ...prev, 
      role_id: roleId
    }));
    setError(null); // Clear error if validation passes
  };

  const fetchRolePermissions = useCallback(async (roleId: number) => {
    try {
      if (!selectedCompany?.company_id) {
        console.error('No company selected');
        return;
      }
  
      // Fetch both organization-specific and global permissions in parallel
      const [orgResult, globalResult] = await Promise.all([
        supabase
          .from('role_permissions')
          .select(`
            allowed,
            permissions:permission_id (
              id,
              name,
              description,
              category
            )
          `)
          .eq('role_id', roleId)
          .eq('org_id', selectedCompany.company_id)
          .eq('allowed', true),
        supabase
          .from('role_permissions')
          .select(`
            allowed,
            permissions:permission_id (
              id,
              name,
              description,
              category
            )
          `)
          .eq('role_id', roleId)
          .is('org_id', null)
          .eq('allowed', true)
      ]);
  
      if (orgResult.error || globalResult.error) {
        console.error('Error fetching role permissions:', orgResult.error || globalResult.error);
        return;
      }
  
      // Create a map to merge permissions, with org-specific taking precedence
      const permissionMap = new Map<string, any>();
  
      // First, add all global permissions
      (globalResult.data || []).forEach((rp: any) => {
        const permission = rp.permissions;
        if (permission && permission.id) {
          permissionMap.set(permission.id.toString(), permission);
        }
      });
  
      // Then, override with organization-specific permissions (these take precedence)
      (orgResult.data || []).forEach((rp: any) => {
        const permission = rp.permissions;
        if (permission && permission.id) {
          permissionMap.set(permission.id.toString(), permission);
        }
      });
  
      // Convert map to array and group by category
      const grouped: { [category: string]: string[] } = {};
      permissionMap.forEach((permission) => {
        if (permission && permission.category) {
          if (!grouped[permission.category]) {
            grouped[permission.category] = [];
          }
          grouped[permission.category].push(permission.description || permission.name);
        }
      });
  
      // Convert to array format for easier rendering
      const categoryArray = Object.entries(grouped).map(([category, permissions]) => ({
        category,
        permissions
      }));
  
      setRolePermissions(prev => ({
        ...prev,
        [roleId]: categoryArray
      }));
    } catch (error) {
      console.error('Error fetching role permissions:', error);
    }
  }, [selectedCompany?.company_id]);

  // Fetch permissions when role changes
  useEffect(() => {
    if (formData.role_id) {
      // Check if we already have permissions for this role
      if (!rolePermissions[formData.role_id]) {
        fetchRolePermissions(formData.role_id);
      }
    }
  }, [formData.role_id, rolePermissions, fetchRolePermissions]);

  // Add this useEffect
  useEffect(() => {
    fetchRoleMapping();
  }, [fetchRoleMapping]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (editingMember) {
        // Update existing member
        await handleUpdateMember();
      } else {
        // Invite new member
        await handleInviteMember();
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteMember = async () => {
    try {
      // Prevent inviting as owner if owner already exists
      if (formData.role_id === 1) {
        const existingOwner = teamMembers.find(m => m.role_id === 1);
        if (existingOwner) {
          setError('Cannot invite as owner. There is already an owner in this organization. Only one owner is allowed.');
          return;
        }
      }

      setLoading(true);
      setError(null);
      setSuccessMsg(null);
  
      // Call database function
      const { data, error } = await supabase.rpc('invite_user_to_organization', {
        p_email: formData.email,
        p_org_id: selectedCompany.company_id,
        p_role_id: formData.role_id,
      });
  
      if (error) throw error;
  
      if (!data || data.success === false) {
        throw new Error(data?.error || 'Failed to process invitation');
      }
  
      if (data.action === 'added_directly') {
        setSuccessMsg(`${formData.email} has been successfully added to ${selectedCompany.company_name}.`);
      } else if (data.action === 'invitation_created') {
        // ✅ Get invitation_id from response
        const invitationId = data.invitation_id;
        
        if (!invitationId) {
          throw new Error('Invitation created but no invitation ID returned');
        }
        
        // ✅ Use Supabase built-in invite (via Edge Function)
        const emailResult = await supabase.functions.invoke('send-invitation-email', {
          body: {
            display_name: formData.name,
            email: formData.email,
            org_id: selectedCompany.company_id,
            role_id: formData.role_id,
            invitation_id: invitationId,
            org_name: selectedCompany.company_name
          }
        });
      
        if (emailResult.error) {
          setError(`Failed to send invitation: ${emailResult.error.message || 'Unknown error'}`);
          return;
        }
      
        if (emailResult.data?.success && emailResult.data?.emailSent) {
          setSuccessMsg(`Invitation email sent to ${formData.email}. They will receive an email to complete their profile.`);
        } else {
          setError(`Failed to send invitation: ${emailResult.data?.error || 'Unknown error'}`);
          return;
        }
      }
  
      setTimeout(() => {
        setSuccessMsg(null);
        onClose();
      }, 3000);
  
    } catch (err: any) {
      setError(`❌ ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMember = async () => {
    try {
      // Prevent changing FROM owner role
      if (editingMember.role_id === 1 && formData.role_id !== 1) {
        setError('Owner role cannot be changed. There must always be one owner in the organization.');
        return;
      }

      // Prevent changing TO owner role if owner already exists
      if (formData.role_id === 1 && editingMember.role_id !== 1) {
        const existingOwner = teamMembers.find(m => m.role_id === 1 && m.id !== editingMember.id);
        if (existingOwner) {
          setError('Cannot assign owner role. There is already an owner in this organization. Only one owner is allowed.');
          return;
        }
      }

      // Only Owner and Admin can update role
      if ([1, 2].includes(currentUserRoleId) && canEditField('role')) {
        const { error: memberError } = await supabase
          .from('organization_members')
          .update({ role_id: formData.role_id })
          .eq('user_id', editingMember.id)
          .eq('org_id', selectedCompany.company_id);
  
        if (memberError) throw memberError;
      }

      // Update user profile (name, phone, and email if allowed)
      const updateData: any = {};
      if (canEditField('name')) {
        updateData.display_name = formData.name;
      }
      if (canEditField('phone')) {
        updateData.contact_number = `${countryPhoneCode}${formData.phone}`;
      }

      // Handle email update
      if (canEditField('email') && formData.email !== editingMember.email) {
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        
        if (!currentUser) {
          throw new Error('Unable to verify current user');
        }

        // If user is updating their own email, use auth.updateUser
        if (editingMember.isCurrentUser) {
          const { error: emailError } = await supabase.auth.updateUser({
            email: formData.email
          });
          
          if (emailError) {
            throw new Error(`Failed to update email: ${emailError.message}`);
          }
        } else {
          // If Owner/Admin is updating someone else's email, use Edge Function
          const { data, error: functionError } = await supabase.functions.invoke('update-user-email', {
            body: {
              user_id: editingMember.id,
              new_email: formData.email,
              current_user_id: currentUser.id
            }
          });

          if (functionError) {
            throw new Error(`Failed to update email: ${functionError.message}`);
          }

          if (!data || !data.success) {
            throw new Error(data?.error || 'Failed to update email');
          }
        }
      }

      if (Object.keys(updateData).length > 0) {
        const { error: profileError } = await supabase
          .from('user_profiles')
          .update(updateData)
          .eq('id', editingMember.id);
  
        if (profileError) throw profileError;
      }

      onSave({
        ...editingMember,
        name: formData.name,
        role: ([1, 2].includes(currentUserRoleId) && canEditField('role')) ? formData.role_id : editingMember.role_id,
        phone: formData.phone,
        email: canEditField('email') ? formData.email : editingMember.email
      });

      setSuccessMsg("Profile updated successfully")
      setTimeout(() => {
        setSuccessMsg(null);
        onClose();
      }, 1000);
    } catch (error: any) {
      setError(error.message || 'Failed to update profile');
    }
  };

  const fetchCountries = useCallback(async () => {
    try {
        const { data, error } = await supabase
            .from('countries')
            .select('iso, name, country_phone_code')
            .order('name', { ascending: true });

        if (error) {
            console.error('Error fetching countries:', error);
            return;
        }

        setCountries(data || []);
    } catch (error) {
        console.error('Error fetching countries:', error);
    }
  }, []);

  const sendPasswordResetEmail = async (email: string, userName: string) => {
    try {
      // Use Supabase's built-in password reset email (with custom template)
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/Reset-Password`,
      });
  
      if (error) {
        console.error('Error sending reset email:', error);
        return {
          success: false,
          emailSent: false,
          error: error.message || 'Failed to send password reset email'
        };
      }
  
      return {
        success: true,
        emailSent: true
      };
    } catch (err: any) {
      console.error('Failed to send password reset email:', err);
      return {
        success: false,
        emailSent: false,
        error: err.message || 'Failed to send email'
      };
    }
  };

  const handleResetPassword = async () => {
    if (!editingMember?.email) {
      setError('No email address found for this member');
      return;
    }

    setResettingPassword(true);
    setError(null);
    setSuccessMsg(null);

    try {
      const result = await sendPasswordResetEmail(
        editingMember.email,
        editingMember.name || editingMember.email.split('@')[0]
      );

      if (result.success && result.emailSent) {
        setSuccessMsg(`Password reset email sent to ${editingMember.email}. Please check their inbox.`);
      } else {
        setError(`Failed to send password reset email: ${result.error || 'Unknown error'}`);
      }

      setTimeout(() => {
        setSuccessMsg(null);
      }, 5000);
    } catch (err: any) {
      setError(`Failed to send password reset email: ${err.message}`);
    } finally {
      setResettingPassword(false);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[95vh] overflow-y-auto relative z-[9999]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {editingMember ? 
              (editingMember.isCurrentUser ? 'Edit Your Profile' : 'Edit Team Member') 
              : 'Invite Team Member'}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {editingMember ? 
              (editingMember.isCurrentUser ? 'Update your personal information' : 'Update member information') 
              : 'Add a new member to your team'}
          </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Member Profile Section */}
            <div>
              <h3 className="text-lg font-semibold text-blue-600 mb-4">Member Profile</h3>
              
              <div className="space-y-4">
                {/* Display Name - Only show when editing existing member */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Enter display name"
                    required
                    disabled={editingMember ? !canEditField('name') : false}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="example@company.com"
                    required
                    disabled={editingMember ? true : false} // Always disabled when editing
                  />
                  {editingMember && (
                    <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                  )}
                </div>

              {editingMember && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="flex">
                  <div className="relative">
                    <select
                      value={countryPhoneCode}
                      onChange={(e) => setCountryPhoneCode(e.target.value)}
                      className={`
                        appearance-none 
                        border border-gray-300 
                        rounded-l-lg 
                        bg-white 
                        text-gray-700 
                        pl-4 pr-8 py-2.5 
                        text-sm 
                        focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                        focus:outline-none 
                        w-26
                        cursor-pointer 
                        transition-all duration-150 
                        hover:border-gray-400 
                        disabled:bg-gray-100 disabled:cursor-not-allowed
                      `}
                      disabled={!canEditField('phone')}
                    >
                      {countries.map(country => (
                        <option key={country.iso} value={country.country_phone_code}>
                            {country.iso} {country.country_phone_code}
                        </option>
                      ))}
                    </select>
                    {/* Custom dropdown arrow */}
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => {
                      // Only allow numbers
                      const phoneValue = e.target.value.replace(/[^0-9]/g, '');
                      handleInputChange('phone', phoneValue);
                    }}
                    className={`
                      flex-1 
                      border border-l-0 border-gray-300 
                      rounded-r-lg 
                      px-4 py-2.5 
                      text-sm 
                      focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                      focus:outline-none 
                      transition-all duration-150 
                      hover:border-gray-400 
                      disabled:bg-gray-100 disabled:cursor-not-allowed
                    `}
                    required
                    placeholder="123456789"
                    disabled={!canEditField('phone')}
                  />
                </div>
              </div>)}

              {/* Reset Password Button - Only show when editing a member */}
              {editingMember && canResetPassword() && (
                <div>
                  <button
                    type="button"
                    onClick={handleResetPassword}
                    disabled={resettingPassword || !editingMember?.email}
                    className="f-btn f-btn-success"
                  >
                    {resettingPassword ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                        <span>Reset Password</span>
                      </>
                    )}
                  </button>
                  <p className="text-xs text-gray-500 mt-1">
                    {editingMember.isCurrentUser 
                      ? 'Send a password reset link to your email'
                      : `Send a password reset link to ${editingMember.email}`
                    }
                  </p>
                </div>
              )}

            </div>
            </div>

            {/* Role & Permissions Section */}
            <div>
              <h3 className="text-lg font-semibold text-blue-600 mb-4">Role & Permissions</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.role_id}
                    onChange={(e) => handleRoleChange(Number(e.target.value))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                      disabled:bg-gray-100 disabled:cursor-not-allowed"
                    required
                    disabled={!canEditField('role') || (editingMember && editingMember.role_id === 1 && formData.role_id === 1)}
                  >
                    {getAvailableRoles().map(roleId => {
                      const isOwnerDisabled = roleId === 1 && (
                        (!editingMember && teamMembers.some(m => m.role_id === 1)) ||
                        (editingMember && editingMember.role_id !== 1 && teamMembers.some(m => m.role_id === 1 && m.id !== editingMember.id))
                      );
                      
                      return (
                        <option 
                          key={roleId} 
                          value={roleId}
                          disabled={isOwnerDisabled}
                        >
                          {roleIdToName[roleId] ? roleIdToName[roleId].charAt(0).toUpperCase() + roleIdToName[roleId].slice(1) : 'Unknown'}
                          {isOwnerDisabled ? ' (Owner already exists)' : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Role Description - Dynamic from Database (Compact Grid with CSS Hover Tooltip) */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Role Permissions</h4>
                  {rolePermissions[formData.role_id] && rolePermissions[formData.role_id].length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {rolePermissions[formData.role_id].map((categoryGroup, idx) => (
                        <div
                          key={idx}
                          className="flex items-center text-sm text-gray-700 relative group cursor-help"
                        >
                          <svg className="w-4 h-4 text-green-500 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="font-medium">{categoryGroup.category}</span>
                          <span className="text-gray-500 ml-1">({categoryGroup.permissions.length})</span>
                          
                          {/* Tooltip - CSS based with grey background */}
                          <div className="absolute left-0 bottom-full mb-2 z-50 bg-gray-700 text-white text-xs rounded-lg shadow-xl p-3 min-w-[200px] max-w-[300px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none">
                            <div className="font-semibold mb-2 text-white uppercase text-xs tracking-wide border-b border-gray-600 pb-1">
                              {categoryGroup.category}
                            </div>
                            <ul className="space-y-1 max-h-[300px] overflow-y-auto">
                              {categoryGroup.permissions.map((permission, pIdx) => (
                                <li key={pIdx} className="flex items-start">
                                  <svg className="w-3 h-3 text-green-400 mr-1.5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                  </svg>
                                  <span className="text-gray-100">{permission}</span>
                                </li>
                              ))}
                            </ul>
                            {/* Arrow pointing down (below the tooltip) */}
                            <div className="absolute top-full left-4 mt-0">
                              <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-700"></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 italic">
                      {formData.role_id ? 'Loading permissions...' : 'Select a role to view permissions'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Success Message */}
          {successMsg && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
              <div className="bg-white rounded-lg p-8 max-w-md w-full shadow-xl">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0">
                    <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-semibold text-gray-900">Success!</h3>
                  </div>
                </div>
                <p className="text-gray-800 mb-6 font-medium">
                  {successMsg}
                </p>
                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      setSuccessMsg('');
                      onClose();
                    }}
                    className="f-btn f-btn-success"
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          )}
          

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="f-btn f-btn-soft"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="f-btn f-btn-primary"
            >
              {loading ? 'Saving...' : editingMember ? 'Save Changes' : 'Invite Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};